import { listBrands } from "@/graphql/queries";
import { useAppDispatch } from "@/store/hooks";
import { setBrands, setBrandsLoading } from "@/store/slices/appSlice";
import { generateClient } from "@/utils/helper/aws-generateClient-singleton";
import { fetchAllWithPagination } from "@/utils/helper/graphql-helper";
import { useEffect, useMemo, useState, useRef } from "react";

const client = generateClient();

const useFetchBrands = ({
  variablesP,
  queryP,
  skip,
}: {
  variablesP?: any;
  queryP?: any;
  skip?: boolean;
}) => {
  const [localBrands, setLocalBrands] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);

  const variables = useMemo(() => {
    return (
      variablesP || {
        filter: {
          is_active: { eq: true },
          is_deleted: { eq: false },
        },
      }
    );
  }, [variablesP]);

  useEffect(() => {
    if (skip) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const fetchBrands = async () => {
      dispatch(setBrandsLoading(true));
      setLoading(true);
      try {
        const data = await fetchAllWithPagination({
          query: queryP || listBrands,
          variables,
          setLoader: setLoading,
          showToast: true,
          client,
          signal: abortControllerRef.current?.signal,
        });

        if (!abortControllerRef.current?.signal.aborted) {
          data.sort((a: any, b: any) => a.name.localeCompare(b.name));
          dispatch(setBrands(data));
          dispatch({ type: "seller/setBrands", payload: data });
          setLocalBrands(data);
        }
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setError(err.message);
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          setLoading(false);
          dispatch(setBrandsLoading(false));
        }
      }
    };

    fetchBrands();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [queryP, variables, skip, dispatch]);

  return { localBrands, loading, error };
};

export default useFetchBrands;
